
import { useRecordCalls } from '#record/calls'
import { includesDelisted, isLive, SearchCondition, SearchDto } from '#search/model'
import { utils } from 'apprise-core/utils'
import { useLanguage, useT } from 'apprise-frontend-core/intl/language'
import { usePrevious } from 'apprise-frontend-core/utils/hooks'
import { useAsyncTask } from 'apprise-ui/utils/asynctask'
import React from 'react'
import { SearchStateContext } from './context'



export const useSearch = () => {

    const t = useT()


    const context = React.useContext(SearchStateContext)

    const task = useAsyncTask()
    const calls = useRecordCalls()
    const lang = useLanguage()

    const previous = usePrevious(context.get())

    // adds logic to run queries
    const self = {

        ...context

        ,

        call: async (dto: SearchDto) => await calls.search(dto)

        ,

        getSearchDto: () : SearchDto => {

            const search= context.get()

            const queryHasRunBefore = previous && utils().deepequals(search.query, previous.query)

            // a pill outside its declared mode keeps its conditions in the query, so the user's input
            // survives a mode switch, but contributes none to the search. this is the only place the
            // query becomes a dto, so it covers the results and the download alike.
            const inScope = (key: string) => isLive(search.pillRegistry?.[key], search.query)

            const preFlightConditions = Object.keys(search.query.conditions).filter(inScope).reduce(
                (acc, cur) => {
                    const pill = search.pillRegistry?.[cur]
                    const resolved = pill?.preFlight ? pill.preFlight(search.query.conditions[cur], search.query) : search.query.conditions[cur]
                    return {...acc, [cur] : resolved}
                }, {} as Record<string, SearchCondition[]>
            )

            const conditions = Object.values(preFlightConditions ?? {}).flat()
            const sort = search.sort?.map(s => ({ field:s.key as string, mode: s.mode!})) ?? []
            const page = queryHasRunBefore ? search.page : 1

            const dto: SearchDto = { 

                mode: search.query.mode,
                scope: search.query.scope,
                sort,
                conditions, 
                includeDelisted: includesDelisted(search.query),
                cursor: { page, pageSize:search.pageSize },
                language: lang.current(),
           
            }

            return dto
        }

        ,

        runQueryQuietly: async () => {

            const search= context.get()

            if (!search.query)
                return

            const queryHasRunBefore = previous && utils().deepequals(search.query, previous.query)

            const page = queryHasRunBefore ? search.page : 1
           
            const dto = self.getSearchDto()

            if (queryHasRunBefore)
                dto.total = search.total

            const { results, total } = await self.call(dto)
            
            
            context.set(s => {

                s.results = results

                if (!queryHasRunBefore){
                    s.total = total 
                    s.page = page
                }
            })
        }

    }


    // split api or typing for task.make gets confused when functions call each other via self.
    return {

        ...self,

        runQuery : task.make(self.runQueryQuietly).with($ =>

            $.show(t('search.searching'))
                .throw({

                    title: t('search.search_error_title'),
                    message: t('search.search_error_msg')

                })

        ).done()
    }

}
